import { EventConfig, EventFactory } from '@cp/shared/tracking';
import { ComponentType, MouseEventHandler, createElement, useCallback } from 'react';
import { useTracking } from './useTracking';

type WithTrackClickProps<T, E, F extends boolean> = T &
  (F extends true ? (E extends void ? {} : { eventParams: E } | { getEventParams: () => E }) : { event: EventConfig });

/**
 * Wrap component with click handler
 * @param Component original component
 * @param eventFactory event to trigger
 */
export const withTrackClick =
  <P extends { onClick?: MouseEventHandler<HTMLElement> }, EventPayload, F extends boolean>(
    Component: ComponentType<P>,
    eventFactory?: F extends true ? EventFactory<EventPayload, EventPayload> : undefined,
  ) =>
  (props: WithTrackClickProps<P, EventPayload, F>) => {
    const { onClick: onClickOriginal, ...restProps } = props;
    const eventParams = 'eventParams' in props ? props.eventParams : undefined;
    const getEventParams = 'getEventParams' in props ? props.getEventParams : undefined;
    const eventConfig = 'event' in props ? props.event : { name: 'not-configured-event' };
    const trigger = useTracking(eventFactory || eventConfig);
    const onClick: MouseEventHandler<HTMLElement> = useCallback(
      (e) => {
        try {
          trigger(getEventParams ? getEventParams() : (eventParams as EventPayload));
        } catch (err) {
          console.error(err);
        } finally {
          onClickOriginal?.(e);
        }
      },
      [trigger, onClickOriginal, eventParams, getEventParams],
    );
    return createElement(Component, { ...restProps, onClick } as unknown as P);
  };
